<script lang="ts">
	import { Asset } from '@atoms';
	import { type AssetWithContentBlock } from './AssetWithContentBlockQuery';
	import { breakpoints } from '@core/tools/Breakpoints.svelte';
	import BlockRenderer from '@comp/BlockRenderer.svelte';
	import { ID_TO_BLOCK } from '@blocks/content/AllContentBlocks';
	import { setContext } from 'svelte';
	import { createViewController, ViewController } from '$lib/actions/animations/ViewController';
	import { EnterOnView } from '$lib/actions/animations/EnterOnView';

	const { data }: { data: AssetWithContentBlock } = $props();

	const mediaFirst = $derived.by(() => {
		return (
			data.layout === 'fullbleed' ||
			data.layout === 'bottom-right' ||
			data.layout === 'side-by-side-right' ||
			!breakpoints.md
		);
	});

	createViewController();

	setContext('content-layout', data.layout);
</script>

<section data-layout={data.layout} use:ViewController>
	<div class="container" data-layout={data.layout} use:EnterOnView>
		{#snippet media()}
			<div class="media">
				<Asset data={data.media} />
			</div>
		{/snippet}

		{#snippet content()}
			<div class="content-container theme-{data.theme}">
				<div class="content">
					<BlockRenderer
						sections={data.content}
						sectionMap={ID_TO_BLOCK}
						caller="asset_with_content"
						theme={data.theme}
					/>
				</div>
			</div>
		{/snippet}
		{#if mediaFirst}
			{@render media()}
			{@render content()}
		{:else}
			{@render content()}
			{@render media()}
		{/if}
	</div>
</section>

<style lang="scss">
	$margin-bottom: 0px;

	section {
		padding-bottom: 16px;
		&:not([data-layout='fullbleed']) {
			@include blockPadding(true);
		}
	}

	.container {
		position: relative;
		@include layoutBlockGrid;

		.media {
			grid-column: 1 / -1;
		}

		.content-container {
			align-self: start;
			grid-column: 1 / -1;
			background-color: hsl(var(--color-foreground));
			color: hsl(var(--color-background));

			&.theme-light {
				background-color: hsl(var(--color-primary));
				color: hsl(var(--color-foreground));
			}

			&.theme-faded {
				background-color: hsl(var(--color-backgroundMuted));
				color: hsl(var(--color-foreground));
			}
		}

		.content {
			position: relative;
			display: flex;
			width: 100%;
			padding: 40px;

			@include md {
				padding: 50px;
			}
		}

		&[data-layout='top-left'] {
			@include md {
				margin-bottom: calc($margin-bottom + 78px);
				.media {
					grid-column: 7 / span 6;
					transform: translateY(78px);
				}

				.content-container {
					grid-column: 1 / span 6;
					margin-right: calc(cols(6, 1, false, true) * -1);
				}
			}
		}

		&[data-layout='bottom-left'] {
			@include md {
				margin-bottom: calc($margin-bottom + 78px);

				.media {
					grid-column: 7 / span 6;
				}

				.content {
					padding-right: calc(cols(7, 3, false, true));
				}

				.content-container {
					grid-column: 1 / span 6;
					margin-right: calc(cols(6, 4, false, true) * -1);
					transform: translateY(78px);
				}
			}
		}

		&[data-layout='bottom-left-front'] {
			@include md {
				margin-bottom: calc($margin-bottom + 67px);

				.media {
					grid-column: 6 / span 8;
				}

				.content-container {
					align-self: end;
					grid-column: 1 / span 5;
					margin-right: calc(cols(5, 1, false, true) * -1);
					transform: translateY(67px);
					z-index: 1;
				}
			}
		}

		&[data-layout='bottom-right'] {
			@include md {
				margin-bottom: calc($margin-bottom + 78px);
				.media {
					grid-column: 1 / span 6;
					z-index: 1;
				}

				.content {
					padding-right: calc(cols(7, 1, false, true));
					padding-left: calc(cols(7, 2, false, true));
				}

				.content-container {
					grid-column: 7 / span 6;
					margin-left: calc(cols(6, 1, false, true) * -1);
					transform: translateY(78px);
				}
			}
		}
		&[data-layout='side-by-side-left'] {
			@include md {
				.media {
					grid-column: 7 / span 6;
				}

				.content-container {
					height: 100%;
					grid-column: 1 / span 6;
				}
			}
		}

		&[data-layout='side-by-side-right'] {
			@include md {
				.media {
					grid-column: 1 / span 6;
				}

				.content-container {
					height: 100%;
					grid-column: 7 / span 6;
				}
			}
		}
		&[data-layout='fullbleed'] {
			@include md {
				.media {
					grid-column: 1 / -1;
					aspect-ratio: 2.6 / 1;
				}

				.content-container {
					position: absolute;
					width: cols(12, 6, false, false);
					right: 0;
					// grid-column: 7 / span 6;
					bottom: 0;
				}
			}
		}
	}
</style>
